import { useState } from 'react';
import { Stack, Button, Alert, Typography, Box, Card } from '@mui/joy';
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup"
import { shoppingListEntrySchema, newShoppingListEntry } from '../schema';
import { useParams } from 'react-router-dom';
import { useCreateIngredientMutation } from '@/features/ingredients/api';
import { useCreateShoppingListEntryMutation } from '../api';
import { IngredientAutocomplete } from '@/features/ingredients/components/IngredientAutocomplete';
import { useSearchIngredientsQuery } from '@/features/ingredients/api';
import { FormAmountUnit } from '@/features/units/components/FormAmountUnit';
import { CategoryPicker } from '@/features/categories/components/CategoryPicker';
import { IngredientNutritionFields } from '@/features/ingredients/components/IngredientNutritionFields';
import AddIcon from '@mui/icons-material/Add';

export function AddShoppingListEntryForm() {
  const { householdId } = useParams();
  const [createIngredient] = useCreateIngredientMutation();
  const [createShoppingListEntry] = useCreateShoppingListEntryMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    defaultValues: newShoppingListEntry(),
    resolver: yupResolver(shoppingListEntrySchema)
  });

  const ingredientName = form.watch('ingredient_name');
  const { data: searchResults } = useSearchIngredientsQuery({ name: ingredientName || '' });

  const [lastPickedIngredientName, setLastPickedIngredientName] = useState('');

  const onAutocompleteSelect = (ingredientName) => {
    setLastPickedIngredientName(ingredientName ?? '');
  };

  const existingIngredient = searchResults?.data?.find(ing => ing.name.toLowerCase() === lastPickedIngredientName?.toLowerCase());
  const isCustomIngredient = lastPickedIngredientName !== '' && !existingIngredient;

  const handleFormKeyDown = (e) => {
    if (e.key !== 'Enter') return;

    const trimmedName = ingredientName?.trim();
    if (!trimmedName) return;

    const matchesExisting = searchResults?.data?.some(ing => ing.name.toLowerCase() === trimmedName.toLowerCase());
    if (!matchesExisting) {
      // A brand-new ingredient needs its details reviewed first — reveal that
      // panel instead of silently creating it with whatever the fields default to.
      e.preventDefault();
      onAutocompleteSelect(trimmedName);
    }
  };

  async function onSubmit(data) {
    setIsSubmitting(true);
    try {
      if (!ingredientName || !ingredientName.trim()) {
        form.setError('ingredient_name', { message: 'Ingredient name is required' });
        return;
      }

      let ingredientId = null;

      if (existingIngredient) {
        ingredientId = existingIngredient.id;
      } else {
        const ingredientResult = await createIngredient({
          data: {
            name: ingredientName.trim(),
            subcategory_id: data.subcategory_id || null,
            kcal: data.kcal,
            protein: data.protein,
            fat: data.fat,
            carbs: data.carbs,
            fiber: data.fiber,
            density: data.density,
            piece_weight: data.piece_weight,
            slice_weight: data.slice_weight,
            package_size: data.package_size,
            portion_size: data.portion_size,
            // Common and preferred unit are curation details set from the
            // Ingredients admin page, not exposed in this quick-add flow.
            common: false,
            preferred_unit: 'g',
          }
        });

        if (ingredientResult.error) {
          console.error('Failed to create ingredient:', ingredientResult.error);
          return;
        }

        ingredientId = ingredientResult.data.data.id;
      }

      const entryResult = await createShoppingListEntry({
        householdId,
        data: {
          ingredient_id: ingredientId,
          subcategory_id: data.subcategory_id || null,
          amount: data.amount,
          unit: data.unit,
          is_bought: false,
        }
      });

      if (entryResult.error) {
        console.error('Failed to create shopping list entry:', entryResult.error);
      } else {
        form.reset(newShoppingListEntry());
        setLastPickedIngredientName('');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const isEmpty = !ingredientName || !ingredientName.trim();

  return (
    <Card>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} onKeyDown={handleFormKeyDown}>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
            <IngredientAutocomplete
              name="ingredient_name"
              placeholder="Search or type new item"
              onBlur={onAutocompleteSelect}
              onSelect={onAutocompleteSelect}
              sx={{ flex: 1 }}
            />
            <FormAmountUnit />
            <Button type="submit" color={isEmpty ? 'neutral' : 'primary'} disabled={isSubmitting || isEmpty} startDecorator={<AddIcon />} loading={isSubmitting} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              Add
            </Button>
          </Stack>
          {isCustomIngredient && (
            <Alert color="neutral" variant="soft" size="md" sx={{ mt: 1.5 }}>
              <Box sx={{ width: '100%' }}>
                <Typography level="body-sm">A new item will be created in:</Typography>
                <CategoryPicker
                  name="subcategory_id"
                  label="Category"
                  sx={{ mt: 1 }}
                />
                <Typography level="body-sm" sx={{ mt: 2 }}>Nutrition and measurements (optional, defaults to 0):</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 1, mt: 1 }}>
                  <IngredientNutritionFields size="sm" />
                </Box>
              </Box>
            </Alert>
          )}
        </form>
      </FormProvider>
    </Card>
  );
}
